/**
 * La galleria "Aggiungi widget", come quella del telefono: ogni widget si
 * presenta con la sua anteprima colorata, una riga che spiega a cosa serve e
 * le taglie in cui esiste. Compaiono solo i widget che il ruolo puo' vedere;
 * quelli gia' presenti (o al massimo di copie) non si aggiungono.
 */

import { Check, Plus } from 'lucide-react';
import type { UserRole } from '../../types/roles';
import { cn } from '../../lib/cn';
import { CATALOGO, GRUPPI, widgetDisponibili, type Taglia, type TipoWidget } from '../../lib/dashboard/catalogo';
import { puoAggiungere, type LayoutDashboard } from '../../lib/dashboard/layout';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { ETICHETTE_TAGLIA } from './taglie';
import { TEMI } from './temi';
import { ICONE_WIDGET } from './widget/registro';

interface Props {
  aperto: boolean;
  onChiudi: () => void;
  ruolo: UserRole;
  layout: LayoutDashboard;
  onAggiungi: (tipo: TipoWidget) => void;
}

const FORMINA: Record<Taglia, string> = { S: 'h-2 w-2', M: 'h-2 w-4', L: 'h-3 w-4', XL: 'h-3 w-6' };

function Anteprima({ tipo }: { tipo: TipoWidget }) {
  const tema = TEMI[tipo];
  const Icona = ICONE_WIDGET[tipo];
  return (
    <div className={cn('relative isolate flex h-24 flex-col justify-between overflow-hidden rounded-2xl p-3 ring-1 ring-black/5', tema.sfondo, tema.testo)}>
      <div aria-hidden className={cn('absolute -right-8 -top-10 h-28 w-28 rounded-full blur-2xl', tema.alone)} />
      <span className={cn('relative flex h-8 w-8 items-center justify-center rounded-xl', tema.icona)}>
        <Icona className="h-4 w-4" />
      </span>
      <span className="relative font-display text-sm font-semibold leading-tight">{CATALOGO[tipo].titolo}</span>
    </div>
  );
}

export function CatalogoWidget({ aperto, onChiudi, ruolo, layout, onAggiungi }: Props) {
  const disponibili = widgetDisponibili(ruolo);

  return (
    <Dialog open={aperto} onOpenChange={(v) => { if (!v) onChiudi(); }}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Aggiungi un widget</DialogTitle>
          <DialogDescription>Scegli cosa vuoi vedere: il widget arriva in fondo, poi lo sposti dove preferisci.</DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          {GRUPPI.map((gruppo) => {
            const tipi = disponibili.filter((t) => CATALOGO[t].gruppo === gruppo.chiave);
            if (tipi.length === 0) return null;
            return (
              <section key={gruppo.chiave}>
                <h3 className="mb-3 font-display text-base font-semibold text-neutral-900">{gruppo.titolo}</h3>
                <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {tipi.map((tipo) => {
                    const def = CATALOGO[tipo];
                    const presenti = layout.widget.filter((w) => w.tipo === tipo).length;
                    const aggiungibile = puoAggiungere(layout, tipo, ruolo);
                    const motivo = presenti >= def.massimo ? (def.massimo === 1 ? 'Già presente' : 'Massimo raggiunto') : 'Dashboard piena';
                    return (
                      <li key={tipo} className="flex flex-col gap-2 rounded-3xl bg-neutral-50 p-2.5 ring-1 ring-neutral-200/70">
                        <Anteprima tipo={tipo} />
                        <p className="px-1 text-xs leading-snug text-neutral-600">{def.descrizione}</p>
                        <div className="mt-auto flex items-center justify-between gap-2 px-1">
                          <span className="flex items-center gap-1 text-neutral-400" title={def.taglie.map((t) => ETICHETTE_TAGLIA[t]).join(', ')}>
                            {def.taglie.map((t) => <span key={t} className={cn('rounded-[2px] border-2 border-current', FORMINA[t])} />)}
                          </span>
                          <button
                            type="button"
                            disabled={!aggiungibile}
                            onClick={() => onAggiungi(tipo)}
                            className="inline-flex h-8 items-center gap-1 rounded-full bg-neutral-900 px-3 text-xs font-semibold text-white transition hover:bg-neutral-700 disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:text-neutral-500"
                          >
                            {aggiungibile ? <><Plus className="h-3.5 w-3.5" /> Aggiungi</> : <><Check className="h-3.5 w-3.5" /> {motivo}</>}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
